import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiArrowLeft, FiCheck, FiBookOpen, FiClock, FiPlay } from 'react-icons/fi';
import { useColorMode } from '../../context/ColorModeContext';
import './StudyPlanPage.css';

interface Topic {
    topic: string;
    ToalEstimatedTimeDays?: number;
    TotalEstimatedTimeDays?: number;
    completed?: boolean;
    completionPercentage?: string;
    subtopics?: Subtopic[];
    studyTips?: string[];
}

interface Subtopic {
    name: string;
    estimatedTimeDays: number;
    completed?: boolean;
}

interface ExamTopic {
    _id: string;
    sessionId: string;
    subject: string;
    examName: string;
    topics: Topic[];
    createdAt: string;
}

const StudyPlanPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { mode } = useColorMode();
    const [topicData, setTopicData] = useState<ExamTopic | null>(null);
    const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>({});

    // Data passed from MyExamTopics navigation
    const state = location.state as { topic: ExamTopic; incompleteTopic?: string; incompleteSubtopic?: string; courseId?: string };

    useEffect(() => {
        if (state && state.topic) {
            setTopicData(state.topic);
            // Expand the first topic or the incomplete topic by default
            if (state.topic.topics && state.topic.topics.length > 0) {
                const initialExpanded: Record<string, boolean> = {};
                if (state.incompleteTopic) {
                    const incompleteIdx = state.topic.topics.findIndex(t => t.topic === state.incompleteTopic);
                    if (incompleteIdx >= 0) {
                        initialExpanded[incompleteIdx] = true;
                    } else {
                        initialExpanded[0] = true;
                    }
                } else {
                    initialExpanded[0] = true;
                }
                setExpandedTopics(initialExpanded);
            }
        } else {
            // If deployed or directly linked without state, user shouldn't be here
            navigate('/dashboard/your-exams');
        }
    }, [state, navigate]);

    if (!topicData) {
        return <div className={`study-plan-container ${mode}`}>Loading...</div>;
    }

    const toggleTopic = (index: number) => {
        setExpandedTopics(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getSubtopicName = (subtopic: Subtopic | string | undefined): string => {
        if (!subtopic) {
            return '';
        }
        return typeof subtopic === 'string' ? subtopic : (subtopic.name || '');
    };

    const calculateOverallProgress = () => {
        if (!topicData.topics || topicData.topics.length === 0) {
            return { percentage: 0, totalSteps: 0, completedSteps: 0, totalTopics: 0, completedTopics: 0, totalEstimatedDays: 0 };
        }

        let totalSteps = 0;
        let completedSteps = 0;
        let completedTopics = 0;

        let totalEstimatedDays = 0;

        topicData.topics.forEach(topic => {
            if (topic.completed) completedTopics++;

            // Add to total days (handle both typos from backend if any)
            const topicDays = topic.TotalEstimatedTimeDays || topic.ToalEstimatedTimeDays || 0;
            totalEstimatedDays += topicDays;

            if (topic.subtopics) {
                topic.subtopics.forEach(sub => {
                    totalSteps++;
                    if (sub.completed) completedSteps++;
                });
            }
        });

        const overallPercentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
        return {
            percentage: overallPercentage,
            totalSteps,
            completedSteps,
            totalTopics: topicData.topics.length,
            completedTopics,
            totalEstimatedDays
        };
    };

    const progress = calculateOverallProgress();

    const handleResumeCourse = () => {
        const fallbackTopic = state.incompleteTopic || topicData.topics?.[0]?.topic || '';
        const matchedTopic = topicData.topics?.find((topic) => topic.topic === fallbackTopic) || topicData.topics?.[0];
        const fallbackSubtopic = state.incompleteSubtopic || getSubtopicName(matchedTopic?.subtopics?.[0]);

        navigate('/chat', {
            state: {
                isExamMode: true,
                courseId: state.courseId || topicData.sessionId || topicData._id,
                subject: topicData.subject,
                examName: topicData.examName,
                incompleteTopic: fallbackTopic || undefined,
                incompleteSubtopic: fallbackSubtopic || undefined,
            },
        });
    };

    return (
        <div className={`study-plan-container ${mode}`}>
            <div className="study-plan-header-area">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FiArrowLeft /> Back
                </button>
                <h1 className="subject-title">{topicData.subject}</h1>
                <div className="exam-meta">
                    <span className="exam-target"><FiCheck className="meta-icon" /> Target Grade: A*</span>
                    <span className="exam-date"><FiClock className="meta-icon" /> Exam: {topicData.examName}</span>
                </div>
            </div>

            <div className="overall-progress-card">
                <div className="progress-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Overall Progress</span>
                        {progress.totalEstimatedDays > 0 && (
                            <span className="exam-date" style={{ fontSize: '0.85rem', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: '12px' }}>
                                <FiClock className="meta-icon" style={{ display: 'inline', marginRight: '4px' }} />
                                {progress.totalEstimatedDays}d total
                            </span>
                        )}
                    </div>
                    <span className="progress-percentage">{progress.percentage}%</span>
                </div>
                <div className="progress-bar-bg">
                    <div className="progress-bar-fill" style={{ width: `${progress.percentage}%` }}></div>
                </div>
                <div className="progress-footer">
                    <span>{progress.completedSteps}/{progress.totalSteps} Steps Completed</span>
                    <span>{progress.completedTopics}/{progress.totalTopics} Sections Completed</span>
                </div>
            </div>

            <div className="topics-accordion-list">
                {topicData.topics && topicData.topics.map((topic, index) => {
                    const isExpanded = !!expandedTopics[index];
                    const subtopics = topic.subtopics || [];
                    const totalSubtopics = subtopics.length;
                    const completedSubtopics = subtopics.filter(s => s.completed).length;
                    const completionPercentage = topic.completionPercentage
                        ? parseInt(topic.completionPercentage)
                        : (totalSubtopics > 0 ? Math.round((completedSubtopics / totalSubtopics) * 100) : 0);

                    return (
                        <div key={index} className={`topic-accordion-item ${isExpanded ? 'expanded' : ''}`}>
                            <div
                                className="topic-accordion-header"
                                onClick={() => toggleTopic(index)}
                            >
                                <div className="topic-accordion-title-area">
                                    <h3>{topic.topic}</h3>
                                    {isExpanded ? <FiChevronUp className="chevron-icon" /> : <FiChevronDown className="chevron-icon" />}
                                </div>
                                {/* Short description if available, otherwise generic */}
                                <p className="topic-accordion-desc">
                                    This section covers key concepts and essential topics related to {topic.topic}. You will review and practice these materials.
                                </p>

                                <div className="topic-progress-row">
                                    <span className="topic-steps-count">{completedSubtopics}/{totalSubtopics} Steps</span>
                                    <span style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        background: "var(--surface-2)",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        fontSize: "0.8rem"
                                    }}>
                                        <FiClock /> {(topic.TotalEstimatedTimeDays || topic.ToalEstimatedTimeDays || 0)}d
                                    </span>
                                    <div className="topic-progress-bg">
                                        <div
                                            className="topic-progress-fill"
                                            style={{ width: `${completionPercentage}%` }}>
                                        </div>
                                    </div>
                                    <span className="topic-percentage">{completionPercentage}%</span>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="topic-accordion-body">
                                    <div className="timeline-container">
                                        {subtopics.map((subtopic, sIdx) => {
                                            const isNext = state.incompleteSubtopic === subtopic.name;
                                            const isCompleted = subtopic.completed;
                                            const isLast = sIdx === subtopics.length - 1;

                                            let nodeClass = "timeline-node";
                                            if (isCompleted) nodeClass += " completed";
                                            else if (isNext) nodeClass += " next";

                                            return (
                                                <div key={sIdx} className="timeline-item">
                                                    <div className="timeline-left">
                                                        <div className={nodeClass}>
                                                            {isCompleted ? <FiCheck size={16} /> : (isNext ? <FiPlay size={14} /> : <FiBookOpen size={14} />)}
                                                        </div>
                                                        {!isLast && <div className={`timeline-line ${isCompleted ? 'completed' : ''}`}></div>}
                                                    </div>
                                                    <div className={`timeline-content ${isNext ? 'next-content' : ''}`}>
                                                        <div className="timeline-content-header">
                                                            <h4>{subtopic.name}</h4>
                                                            {isNext && <span className="next-badge">NEXT</span>}
                                                        </div>
                                                        {/* <p>Study this subtopic carefully.</p> */}
                                                        <span style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            color: "var(--text-secondary)",
                                                            fontSize: "0.85rem",
                                                            background: "var(--surface-1)",
                                                            padding: "4px 8px",
                                                            borderRadius: "12px",
                                                            marginTop: "8px",
                                                            fontWeight: 500
                                                        }}>
                                                            <FiClock /> {subtopic.estimatedTimeDays}d
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {subtopics.length === 0 && (
                                            <p className="no-subtopics-text">No active subtopics for this module.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="resume-action-container">
                <button className="resume-course-btn" onClick={handleResumeCourse}>
                    Start / Resume Course
                </button>
            </div>
        </div>
    );
};

export default StudyPlanPage;
